import { parseBoolean } from '@tunein/web-utils';
import { mediaFormats } from '../../../constants';
import { getMediaFormat, toHttps } from '../../../utils';
import { isHls, isSubscriptionRequiredStream } from '../utils';
const DEFAULT_STREAM_TIMEOUT = 15000;
const SHOUTCAST_TIMEOUT = 25000;
const NO_SHOUTCAST_REGEX = /(tunein|rfcm).*streamguys|tunein.com|mediatailor/;
export function transformTune({ streams, tuneInPlaylistHandler, forceHttpsStreams, playbackPositionOverride, }) {
    const streamsAfterShoutcastInjection = [];
    const finalStreams = {
        streams: [],
        hadOnlyHttpStreams: true,
    };
    return (Array.isArray(streams) ? streams : [streams])
        .map(mapStreams(tuneInPlaylistHandler, forceHttpsStreams, playbackPositionOverride))
        .map(transformMislabelledHlsStreams)
        .reduce(injectShoutcastStreams, streamsAfterShoutcastInjection)
        .reduce(splitStandaloneStreams, finalStreams);
}
function mapStreams(tuneInPlaylistHandler, forceHttpsStreams, playbackPositionOverride) {
    return (stream) => {
        const { url: rawUrl, reliability, mediaType, playlistType, guideId, nextGuideId, itemToken, position, isProxy, liveSeekStream, isHlsAdvanced, useStreamMetadata, isAdClippedContentEnabled, rejectReason, isBoostStation: isBoostStream, isPreroll, bitrate, } = stream;
        const isTuneInPlaylistFormat = parseBoolean(isProxy);
        const url = forceHttpsStreams ? toHttps(rawUrl) : rawUrl;
        const wasHttpStream = rawUrl?.startsWith('http://');
        const transformedStream = {
            url,
            reliability,
            format: isTuneInPlaylistFormat
                ? mediaFormats.tipls
                : getMediaFormat(playlistType, mediaType),
            id: guideId,
            nextGuideId,
            itemToken,
            bitrate,
            position: playbackPositionOverride ?? (Number(position) || 0),
            isLiveSeekStream: parseBoolean(liveSeekStream),
            isHlsAdvanced: parseBoolean(isHlsAdvanced),
            isSSAI: parseBoolean(isHlsAdvanced),
            useStreamMetadata: parseBoolean(useStreamMetadata),
            isMidrollEnabled: parseBoolean(isAdClippedContentEnabled),
            isBoostStream: parseBoolean(isBoostStream),
            isPrerollStream: parseBoolean(isPreroll),
            isSubscriptionRequiredStream: isSubscriptionRequiredStream(stream),
            timeout: DEFAULT_STREAM_TIMEOUT,
            rejectReason,
            wasHttpStream,
        };
        if (isTuneInPlaylistFormat) {
            transformedStream.playlistHandler = () => tuneInPlaylistHandler(url);
        }
        return transformedStream;
    };
}
function splitStandaloneStreams(streamData, nextStream) {
    if (nextStream.isPrerollStream) {
        streamData.prerollStream = nextStream;
    }
    else if (nextStream.isBoostStream) {
        streamData.boostStream = nextStream;
    }
    else if (nextStream.isSubscriptionRequiredStream) {
        streamData.subscriptionRequiredStream = nextStream;
    }
    else if (nextStream.format === mediaFormats.html) {
        streamData.externalPlayerUrl =
            streamData.externalPlayerUrl || nextStream.url;
    }
    else {
        streamData.hadOnlyHttpStreams =
            streamData.hadOnlyHttpStreams && nextStream.wasHttpStream;
        streamData.streams.push(nextStream);
    }
    return streamData;
}
function applyShoutcastUrlFallback(url = '') {
    const { protocol, pathname, search, host } = new URL(url);
    const shoutcastHack = pathname.endsWith('/') ? ';' : '/;';
    if (pathname.endsWith(shoutcastHack)) {
        return url;
    }
    return `${protocol}//${host}${pathname}${shoutcastHack}${search}`;
}
export function injectShoutcastStreams(streams, nextStream) {
    const { isPrerollStream, isBoostStream, isSubscriptionRequiredStream, url, format, } = nextStream;
    if (isPrerollStream ||
        isBoostStream ||
        isSubscriptionRequiredStream ||
        format === mediaFormats.hls ||
        format === mediaFormats.html ||
        url.includes('/;') ||
        NO_SHOUTCAST_REGEX.test(url)) {
        return [...streams, nextStream];
    }
    const shoutCastStream = {
        ...nextStream,
        isShoutcastUrl: true,
        timeout: SHOUTCAST_TIMEOUT,
        url: applyShoutcastUrlFallback(url),
    };
    return [...streams, nextStream, shoutCastStream];
}
export function transformMislabelledHlsStreams(stream) {
    if (stream.format === mediaFormats.hls) {
        return stream;
    }
    const nextStream = { ...stream };
    if (isHls(nextStream.url)) {
        nextStream.isHlsFormatIncorrect = true;
    }
    const transformedUrl = removeM3UExtension(nextStream.url);
    if (transformedUrl !== nextStream.url) {
        nextStream.url = transformedUrl;
        nextStream.removedM3UExtension = true;
    }
    return nextStream;
}
function removeM3UExtension(url) {
    const m3uIndex = url.indexOf('.m3u');
    return m3uIndex > 0 ? url.slice(0, m3uIndex) : url;
}
export function transformUnwrappedStreams(options) {
    const { streams, forceHttpsStreams } = options;
    return streams
        .map(({ url, reliability, playlistType, mediaType, streamId }) => ({
        url: forceHttpsStreams ? toHttps(url) : url,
        reliability,
        format: getMediaFormat(playlistType, mediaType),
        id: `e${streamId}`,
        wasHttpStream: url?.startsWith('http://'),
    }))
        .map(transformMislabelledHlsStreams)
        .reduce(injectShoutcastStreams, []);
}
